import { useCallback, useEffect, useRef, useState } from 'react'
import { CheckCircle2, CreditCard, LoaderCircle, ShieldCheck, Smartphone } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { fetchCurrentSession, fetchMembershipSummary } from '@/features/auth/services/auth-service'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import { buildDeviceFingerprint } from '@/features/auth/utils/device-fingerprint'
import {
  createBillingOrder,
  fetchBillingOptions,
  fetchBillingOrderStatus,
} from '@/features/billing/services/billing-service'
import { useMembershipDialogStore } from '@/features/billing/stores/membership-dialog-store'
import {
  formatBillingCycleLabel,
  formatBillingPriceLabel,
} from '@/features/billing/utils/membership-labels'
import type {
  BillingChannelAvailability,
  BillingCreditPack,
  BillingOrderResult,
  BillingPlan,
} from '@/features/billing/types'
import './membership-purchase-dialog.css'

type Provider = 'alipay' | 'wechat'
type PurchaseTarget =
  | { kind: 'subscription'; item: BillingPlan }
  | { kind: 'credit_pack'; item: BillingCreditPack }

const fallbackAvailability: BillingChannelAvailability = {
  alipay: false,
  wechat: false,
  notifyBaseUrl: '',
  alipayMessage: '支付宝暂不可用',
  wechatMessage: '微信支付暂不可用',
}

function isPurchasablePlan(plan: BillingPlan) {
  return !plan.plan_code.includes('free')
}

function targetLabel(target: PurchaseTarget) {
  return target.kind === 'subscription' ? target.item.plan_name : target.item.packName
}

function targetPrice(target: PurchaseTarget) {
  return target.kind === 'subscription'
    ? `${formatBillingPriceLabel(target.item)} ${formatBillingCycleLabel(target.item)}`
    : `¥${target.item.price}`
}

export function MembershipPurchaseDialog() {
  const open = useMembershipDialogStore((state) => state.open)
  const closeDialog = useMembershipDialogStore((state) => state.closeDialog)
  const preferredPlanCode = useMembershipDialogStore((state) => state.preferredPlanCode)
  const preferredCreditPackCode = useMembershipDialogStore((state) => state.preferredCreditPackCode)
  const surface = useMembershipDialogStore((state) => state.surface)
  const setSession = useAuthSessionStore((state) => state.setSession)
  const updateMembership = useAuthSessionStore((state) => state.updateMembership)
  const [target, setTarget] = useState<PurchaseTarget | null>(null)
  const [availability, setAvailability] = useState<BillingChannelAvailability>(fallbackAvailability)
  const [provider, setProvider] = useState<Provider>('alipay')
  const [order, setOrder] = useState<BillingOrderResult | null>(null)
  const [fingerprint, setFingerprint] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [error, setError] = useState('')
  const [paid, setPaid] = useState(false)
  const orderRequestIdRef = useRef(0)

  const reset = useCallback(() => {
    orderRequestIdRef.current += 1
    setTarget(null)
    setProvider('alipay')
    setOrder(null)
    setLoading(false)
    setLoadingOptions(false)
    setError('')
    setPaid(false)
  }, [])

  const close = useCallback(() => {
    reset()
    closeDialog()
  }, [closeDialog, reset])

  useEffect(() => {
    void buildDeviceFingerprint().then(setFingerprint).catch(() => setFingerprint(''))
  }, [])

  useEffect(() => {
    if (!open) return

    let cancelled = false
    orderRequestIdRef.current += 1
    setLoadingOptions(true)
    setError('')
    setOrder(null)
    setPaid(false)

    void fetchBillingOptions()
      .then((payload) => {
        if (cancelled) return
        setAvailability(payload.availability)
        const preferredCreditPack = payload.creditPacks?.find(
          (pack) => pack.packCode === preferredCreditPackCode,
        )
        const preferredPlan = payload.plans.find((plan) => plan.plan_code === preferredPlanCode)
        const defaultPlan = payload.plans.find(isPurchasablePlan)

        if (preferredCreditPack) {
          setTarget({ kind: 'credit_pack', item: preferredCreditPack })
        } else if (preferredPlan && isPurchasablePlan(preferredPlan)) {
          setTarget({ kind: 'subscription', item: preferredPlan })
        } else if (defaultPlan) {
          setTarget({ kind: 'subscription', item: defaultPlan })
        } else {
          setError('当前没有可购买的套餐，请稍后再试。')
        }
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError instanceof Error ? requestError.message : '加载支付信息失败')
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, preferredCreditPackCode, preferredPlanCode])

  const createOrder = useCallback(async (nextProvider: Provider) => {
    if (!target) return

    const requestId = ++orderRequestIdRef.current
    setProvider(nextProvider)
    setLoading(true)
    setError('')
    setOrder(null)
    try {
      const nextOrder = await createBillingOrder({
        ...(target.kind === 'subscription'
          ? { planCode: target.item.plan_code }
          : { creditPackCode: target.item.packCode }),
        provider: nextProvider,
        deviceFingerprint: fingerprint,
        source: 'subsite-videocut',
      })
      if (requestId !== orderRequestIdRef.current) return
      setOrder(nextOrder)
      if (nextOrder.paymentMode === 'redirect' && nextOrder.paymentUrl) {
        window.open(nextOrder.paymentUrl, '_blank', 'noopener,noreferrer')
      }
    } catch (requestError) {
      if (requestId === orderRequestIdRef.current) {
        setError(requestError instanceof Error ? requestError.message : '创建支付订单失败，请重试。')
      }
    } finally {
      if (requestId === orderRequestIdRef.current) setLoading(false)
    }
  }, [fingerprint, target])

  useEffect(() => {
    if (!open || !target || loadingOptions || loading || order || error || paid) return
    const defaultProvider: Provider = availability.alipay ? 'alipay' : 'wechat'
    if (!availability[defaultProvider]) return
    void createOrder(defaultProvider)
  }, [availability, createOrder, error, loading, loadingOptions, open, order, paid, target])

  useEffect(() => {
    if (!open || !order?.orderNo || paid) return

    let stopped = false
    const checkStatus = async () => {
      try {
        const payload = await fetchBillingOrderStatus(order.orderNo)
        if (stopped || payload.order?.status !== 'paid') return
        setPaid(true)
        const [latestSession, membership] = await Promise.all([
          fetchCurrentSession(),
          fetchMembershipSummary(),
        ])
        if (latestSession) setSession(latestSession)
        if (membership) updateMembership(membership)
      } catch {
        // Keep polling: a transient status failure should not discard a valid pending order.
      }
    }

    void checkStatus()
    const timer = window.setInterval(() => void checkStatus(), 3000)
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [open, order?.orderNo, paid, setSession, updateMembership])

  const paymentUnavailable = !availability.alipay && !availability.wechat
  const workspaceSurface = surface === 'workspace'
  const activeProvider = order?.provider ?? provider
  const paymentCopy = activeProvider === 'alipay' ? '支付宝' : '微信'

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && close()}>
      <DialogContent className={`membership-checkout max-w-[760px] overflow-hidden border-[#dfe3ec] bg-white p-0 text-[#101727] sm:rounded-2xl${workspaceSurface ? ' membership-checkout--workspace' : ''}`}>
        {paid && target ? (
          <div className="px-6 py-14 text-center sm:px-14">
            <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-[#ecfdf3] text-[#16a34a]">
              <CheckCircle2 className="size-9" />
            </div>
            <DialogTitle className="mt-6 text-3xl font-semibold tracking-tight">支付成功</DialogTitle>
            <DialogDescription className="mt-3 text-base leading-7 text-[#697184]">
              {target.kind === 'subscription' ? '会员权益已更新，可以开始使用 JIAN。' : '高级生成点数已到账。'}
            </DialogDescription>
            <Button type="button" className="mt-8 rounded-full px-7" onClick={close}>完成</Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-[0.92fr_1.08fr]">
            <div className="border-b border-[#e5e7eb] bg-[#f7f8fc] p-6 md:border-b-0 md:border-r md:p-8">
              <DialogHeader>
                <p className="text-xs font-bold tracking-[.13em] text-[#5255f6]">JIAN SECURE CHECKOUT</p>
                <DialogTitle className="mt-4 text-2xl font-semibold tracking-tight">确认并完成支付</DialogTitle>
                <DialogDescription className="mt-2 leading-6 text-[#697184]">支付完成后，系统会自动刷新你的会员状态与额度。</DialogDescription>
              </DialogHeader>

              <div className="mt-8 rounded-xl border border-[#e0e4ee] bg-white p-5">
                <p className="text-xs font-medium text-[#697184]">本次开通</p>
                <p className="mt-2 text-lg font-semibold">{target ? targetLabel(target) : '正在加载套餐…'}</p>
                <p className="mt-2 text-2xl font-semibold text-[#101727]">{target ? targetPrice(target) : '—'}</p>
                <div className="mt-5 border-t border-[#edf0f5] pt-4 text-sm leading-6 text-[#697184]">
                  <div className="flex gap-2"><ShieldCheck className="mt-0.5 size-4 shrink-0 text-[#5255f6]" />订单金额与权益以当前后端配置为准。</div>
                </div>
              </div>

              <p className="mt-6 text-xs leading-5 text-[#7c8494]">如需更换套餐，请关闭此窗口后重新选择对应套餐。</p>
            </div>

            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-lg font-semibold">选择支付方式</h3>
                {loading && <span className="inline-flex items-center gap-1.5 text-xs text-[#697184]"><LoaderCircle className="size-3.5 animate-spin" />正在生成订单</span>}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <button type="button" disabled={!availability.alipay || loading || !target} onClick={() => void createOrder('alipay')} className={`inline-flex h-12 items-center justify-center gap-2 rounded-xl border text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${activeProvider === 'alipay' ? 'border-[#5255f6] bg-[#f4f4ff] text-[#4245df]' : 'border-[#dfe3ec] bg-white text-[#3d4658] hover:border-[#a8adff]'}`}><CreditCard className="size-4" />支付宝</button>
                <button type="button" disabled={!availability.wechat || loading || !target} onClick={() => void createOrder('wechat')} className={`inline-flex h-12 items-center justify-center gap-2 rounded-xl border text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${activeProvider === 'wechat' ? 'border-[#5255f6] bg-[#f4f4ff] text-[#4245df]' : 'border-[#dfe3ec] bg-white text-[#3d4658] hover:border-[#a8adff]'}`}><Smartphone className="size-4" />微信支付</button>
              </div>

              {paymentUnavailable ? <p className="mt-5 rounded-lg bg-[#fff7ed] px-4 py-3 text-sm text-[#9a3412]">当前没有可用的支付渠道，请稍后再试。</p> : null}
              {error ? <div className="mt-5 rounded-lg border border-[#fecaca] bg-[#fff1f2] px-4 py-3 text-sm text-[#be123c]">{error}<button type="button" className="ml-2 font-semibold underline" onClick={() => void createOrder(provider)}>重新生成</button></div> : null}

              <div className="mt-6 flex min-h-[300px] flex-col items-center justify-center rounded-xl border border-[#e0e4ee] bg-[#fbfcff] p-5 text-center">
                {order?.paymentMode === 'qr' && order.qrCodeUrl ? <>
                  <div className="rounded-lg bg-white p-3 shadow-sm"><QRCodeSVG value={order.qrCodeUrl} size={190} level="M" includeMargin={false} /></div>
                  <p className="mt-4 font-semibold">请使用{paymentCopy}扫码支付</p>
                  <p className="mt-1 text-xs text-[#697184]">支付完成后会自动检测，无需刷新页面。</p>
                  <p className="mt-3 text-xs text-[#8c94a3]">订单号：{order.orderNo}</p>
                </> : order?.paymentMode === 'redirect' && order.paymentUrl ? <>
                  <CreditCard className="size-9 text-[#5255f6]" />
                  <p className="mt-4 font-semibold">已打开{paymentCopy}支付页面</p>
                  <a className="mt-4 text-sm font-semibold text-[#5255f6] underline" href={order.paymentUrl} target="_blank" rel="noreferrer">未打开？继续支付</a>
                  <p className="mt-3 text-xs text-[#8c94a3]">订单号：{order.orderNo}</p>
                </> : <><LoaderCircle className="size-7 animate-spin text-[#5255f6]" /><p className="mt-4 text-sm text-[#697184]">正在生成{paymentCopy}支付二维码…</p></>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
